import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { buildApiUrl } from "../config";
import { useAuth } from "../AuthContext";

const DOC_LABELS = {
  pupil_passport:     "Pupil Passport",
  father_passport:    "Father Passport",
  mother_passport:    "Mother Passport",
  pupil_qid:          "Pupil QID",
  father_qid:         "Father QID",
  mother_qid:         "Mother QID",
  pupil_immunization: "Pupil Immunization",
  eyfs_form:          "EYFS Form",
};

const REQUIRED_TYPES = Object.keys(DOC_LABELS);
const PARENT_DOC_TYPES = new Set(["father_passport", "mother_passport", "father_qid", "mother_qid"]);

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString("en-GB");
};

const isExpired = (iso) => iso && new Date(iso) < new Date();

export default function ParentDocumentsPublicView() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const [authState, setAuthState] = useState("checking"); // checking | denied | allowed
  const [siblingIds, setSiblingIds] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploadingType(docType);
    const form = new FormData();
    form.append("file", file);
    form.append("school_id", schoolId);
    form.append("doc_type", docType);
    form.append("expiry_date", "");
    try {
      const res = await axios.post(buildApiUrl("/api/parent/documents/upload"), form);
      if (res.data.success) {
        setDocs(prev => {
          const filtered = prev.filter(d => d.doc_type !== docType);
          return [...filtered, res.data.document];
        });
      }
    } catch {}
    finally { setUploadingType(null); }
  };

  // Step 1: verify the logged-in parent owns this schoolId
  useEffect(() => {
    if (role !== "parent") {
      setAuthState("login");
      setLoading(false);
      return;
    }
    const parentEmail = localStorage.getItem("parentEmail");
    if (!parentEmail) { setAuthState("login"); setLoading(false); return; }

    axios.get(buildApiUrl("/data/parents.json"))
      .then(res => {
        const matched = res.data.find(p => p.email === parentEmail);
        const childIds = matched?.child_ids || [];
        if (childIds.includes(schoolId)) {
          setSiblingIds(childIds.filter(id => id !== schoolId));
          setAuthState("allowed");
        } else {
          setAuthState("denied");
          setLoading(false);
        }
      })
      .catch(() => { setAuthState("denied"); setLoading(false); });
  }, [role, schoolId]);

  // Step 2: fetch documents only when allowed
  useEffect(() => {
    if (authState !== "allowed") return;
    const fetchAll = async () => {
      try {
        // Fetch this student's docs
        const res = await axios.get(buildApiUrl(`/api/parent/documents/${schoolId}`));
        const ownDocs = res.data.success ? (res.data.documents || []) : [];

        // Fetch sibling docs to pick up shared parent-type documents
        const siblingResults = await Promise.all(
          siblingIds.map(id => axios.get(buildApiUrl(`/api/parent/documents/${id}`)).catch(() => null))
        );
        const siblingDocs = siblingResults
          .filter(Boolean)
          .flatMap(r => (r.data.success ? r.data.documents || [] : []))
          .filter(d => PARENT_DOC_TYPES.has(d.doc_type));

        // Merge: own docs take priority; fill in missing parent docs from siblings
        const ownTypes = new Set(ownDocs.map(d => d.doc_type));
        const merged = [
          ...ownDocs,
          ...siblingDocs.filter(d => !ownTypes.has(d.doc_type)),
        ];
        setDocs(merged);
      } catch {}
      finally { setLoading(false); }
    };
    fetchAll();
  }, [authState, schoolId, siblingIds]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getDoc = (type) => docs.find((d) => d.doc_type === type);
  const received = REQUIRED_TYPES.filter((t) => getDoc(t)).length;

  // ── Auth gate: not logged in ──
  if (authState === "login") return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-sm w-full text-center">
        <span className="text-4xl">🔒</span>
        <h2 className="text-lg font-bold text-gray-800 mt-3 mb-1">Parent Login Required</h2>
        <p className="text-sm text-gray-500 mb-6">Please log in with your parent account to view these documents.</p>
        <button
          onClick={() => { localStorage.setItem("docsReturnUrl", window.location.href); navigate("/login-parent"); }}
          className="w-full py-2.5 bg-[#0f2752] text-white rounded-xl font-semibold text-sm hover:bg-[#1a3a72] transition-colors"
        >
          Log In as Parent
        </button>
      </div>
    </div>
  );

  // ── Auth gate: wrong parent ──
  if (authState === "denied") return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 max-w-sm w-full text-center">
        <span className="text-4xl">🚫</span>
        <h2 className="text-lg font-bold text-gray-800 mt-3 mb-1">Access Denied</h2>
        <p className="text-sm text-gray-500 mb-6">This document link does not belong to your account.</p>
        <button onClick={() => navigate("/parent")} className="w-full py-2.5 bg-[#0f2752] text-white rounded-xl font-semibold text-sm hover:bg-[#1a3a72] transition-colors">
          Go to My Dashboard
        </button>
      </div>
    </div>
  );

  // ── Still checking ──
  if (authState === "checking") return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0f2752] to-[#1a3a72] px-6 py-5 shadow-lg">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📁</span>
            <div>
              <h1 className="text-lg font-bold text-white">Parent Documents</h1>
              <p className="text-xs text-blue-200">Student ID: {schoolId}</p>
            </div>
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-xl transition-colors border border-white/20"
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy Link
              </>
            )}
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Progress bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Documents Submitted</span>
            <span className="text-sm font-bold text-indigo-600">{received} / {REQUIRED_TYPES.length}</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2.5">
            <div
              className="bg-indigo-500 h-2.5 rounded-full transition-all"
              style={{ width: `${(received / REQUIRED_TYPES.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Document list */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 pt-5 pb-3">
            <h2 className="font-bold text-gray-800 text-base">Required Documents</h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {REQUIRED_TYPES.map((type) => {
                const doc = getDoc(type);
                const expired = isExpired(doc?.expiry_date);
                const isUploading = uploadingType === type;
                const inputId = `pub-upload-${type}`;
                return (
                  <div key={type} className={`flex items-center gap-3 px-5 py-3.5 ${expired ? "bg-red-50/40" : doc ? "bg-green-50/40" : ""}`}>
                    {/* Checkbox */}
                    <div className={`w-6 h-6 rounded-md flex-shrink-0 flex items-center justify-center border-2 ${expired ? "bg-red-400 border-red-400" : doc ? "bg-green-500 border-green-500" : "border-gray-300 bg-white"}`}>
                      {doc && (
                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>

                    {/* Label + expiry */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{DOC_LABELS[type]}</p>
                      {doc?.expiry_date && (
                        <p className={`text-xs font-medium mt-0.5 ${expired ? "text-red-500" : "text-gray-400"}`}>
                          Expiry: {formatDate(doc.expiry_date)}
                          {expired && " — EXPIRED"}
                        </p>
                      )}
                    </div>

                    {/* Status + actions */}
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${expired ? "text-red-500" : doc ? "text-green-600" : "text-gray-400"}`}>
                        {expired ? "Upload Again" : doc ? "Received" : "Pending"}
                      </span>
                      {doc && (
                        <>
                          <a
                            href={buildApiUrl(`/api/parent/documents/download/${doc.id}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                            title="Download"
                          >
                            <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </a>
                        </>
                      )}
                      {/* Upload button */}
                      <label htmlFor={inputId}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors border ${
                          isUploading
                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-wait"
                            : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}>
                        {isUploading ? (
                          <span className="w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                        )}
                        <span>Upload</span>
                      </label>
                      <input
                        id={inputId}
                        type="file"
                        className="hidden"
                        accept="image/*,application/pdf"
                        disabled={isUploading}
                        onChange={e => { if (e.target.files[0]) handleUpload(type, e.target.files[0]); e.target.value = ""; }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Sherborne Qatar · Parent Documents Portal
        </p>
      </div>
    </div>
  );
}
